import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { HelpCircle, Search, X } from 'lucide-react';
import InlinePopover from '@/components/InlinePopover';

const ComboHelp = ({ combo }) => (
  <InlinePopover
    maxWidth={280}
    ariaLabel={`What ${combo.label} tests`}
    testId={`true-return-combo-help-${combo.slug}`}
    className="inline-flex shrink-0 text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors"
    render={() => (
      <div className="text-xs leading-relaxed space-y-1 p-1">
        <div className="font-bold font-mono text-foreground">{combo.label}</div>
        <p className="text-foreground/85">{combo.blurb}</p>
      </div>
    )}
  >
    <HelpCircle className="w-3.5 h-3.5" />
  </InlinePopover>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrueReturnCombos({ tickers = [] }) {
  const currentKey = [...tickers].sort().join(',');
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState('');
  useEffect(() => {
    axios.get(`${API}/true-return/combos`).then((r) => setColumns(r.data.columns || [])).catch(() => setColumns([]));
  }, []);
  if (!columns.length) return null;
  const q = filter.trim().toUpperCase();
  const matches = (c) => !q || c.tickers.some((t) => t.startsWith(q));
  const total = columns.reduce((n, col) => n + col.combos.filter(matches).length, 0);
  return (
    <section data-testid="true-return-combos">
      <h2 className="text-base md:text-lg font-bold">Popular head-to-heads</h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">Ready-made comparisons of the ETFs most often held in IRAs and 401(k)s — open one, then swap in your own tickers.</p>
      <div className="relative mb-3 max-w-sm rounded-xl border bg-muted/30 px-3 py-2 focus-within:ring-2 focus-within:ring-sky-400" data-testid="true-return-combo-filter">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter head-to-heads by ticker (SCHD, JEPI, TLT…)"
            className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground font-mono"
            data-testid="true-return-combo-filter-input"
          />
          {q && (
            <button type="button" onClick={() => setFilter('')} className="text-muted-foreground hover:text-foreground p-0.5" aria-label="Clear filter" data-testid="true-return-combo-filter-clear">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {q && total === 0 && (
        <p className="text-xs text-muted-foreground mb-3" data-testid="true-return-combo-filter-empty">No head-to-heads include “{q}” — add it in the picker above to build your own.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {columns.map((col) => (
          <ComboColumn key={col.title} col={col} combos={col.combos.filter(matches)} currentKey={currentKey} />
        ))}
      </div>
    </section>
  );
}

const PHONE_LIMIT = 6;

function ComboColumn({ col, combos, currentKey }) {
  const colKey = col.title.replace(/\W+/g, '-').toLowerCase();
  const storageKey = `true-return-combos-expanded:${colKey}`;
  const [expanded, setExpandedState] = useState(() => sessionStorage.getItem(storageKey) === '1');
  const setExpanded = (fn) => setExpandedState((v) => {
    const next = fn(v);
    sessionStorage.setItem(storageKey, next ? '1' : '0');
    return next;
  });
  const hasMore = combos.length > PHONE_LIMIT;
  return (
    <div className="rounded-xl border bg-card p-3 flex flex-col" data-testid={`true-return-combo-col-${colKey}`}>
      <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{col.title}</h3>
      <ul className="flex-1">
        {combos.map((c, i) => {
          const isCurrent = [...c.tickers].sort().join(',') === currentKey;
          const phoneHidden = !expanded && i >= PHONE_LIMIT ? 'hidden md:flex' : 'flex';
          return (
            <li key={c.slug} className={`${phoneHidden} items-center gap-1 pr-1 rounded-md ${i % 2 === 1 ? 'bg-slate-300/70 dark:bg-slate-800/70' : ''}`}>
              {isCurrent ? (
                <span
                  className="block flex-1 min-w-0 rounded-md px-2 py-1 text-sm font-mono text-muted-foreground/50 cursor-default select-none"
                  aria-current="page"
                  title="This is the comparison you're looking at"
                  data-testid={`true-return-combo-${c.slug}-current`}
                >
                  {c.label}
                </span>
              ) : (
                <Link
                  to={`/true-return/${c.slug}`}
                  state={{ scrollToChart: true }}
                  className="block flex-1 min-w-0 rounded-md px-2 py-1 text-sm font-mono transition-colors hover:bg-muted"
                  data-testid={`true-return-combo-${c.slug}`}
                >
                  {c.label}
                </Link>
              )}
              {c.blurb && <ComboHelp combo={c} />}
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="md:hidden mt-2 mx-auto flex items-center gap-1.5 text-sm font-semibold text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200 transition-colors py-1"
          data-testid={`true-return-combo-toggle-${colKey}`}
        >
          {expanded ? 'Show Less' : `Show More`}
          <span
            aria-hidden="true"
            className={`inline-block w-0 h-0 border-x-[6px] border-x-transparent ${expanded ? 'border-b-[8px] border-b-sky-700 dark:border-b-sky-300' : 'border-t-[8px] border-t-sky-700 dark:border-t-sky-300'}`}
          />
        </button>
      )}
    </div>
  );
}
